import React from 'react';
import * as dpService from 'features/dp/services/dpService';
import IFoodPanel from './IFoodPanel';

/**
 * HoleriteConteudo – the full holerite pay-stub content with editable values.
 *
 * Props:
 *   holerite – full holerite object { funcionario, folha, empresa, proventos, descontos,
 *              informativos, total_proventos, total_descontos, liquido, ifood }
 */

const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const fmtCurrency = (v) =>
  Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtRef = (v) => {
  const n = Number(v || 0);
  return n > 0
    ? n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    : '';
};

export default function HoleriteConteudo({ holerite }) {
  const {
    funcionario: func,
    proventos,
    descontos,
    informativos,
    total_proventos: totalProventos,
    total_descontos: totalDescontos,
    liquido,
  } = holerite;

  const folha = holerite.folha;
  const emp = holerite.empresa || {};
  const competencia = folha?.competencia ?? '';

  // Build month/year display
  const mesAno = competencia
    ? (() => {
        const [y, mo] = competencia.split('-');
        return (MESES[parseInt(mo, 10) - 1] || mo) + ' de ' + y;
      })()
    : '';

  // Compute footer bases
  const salBase = (() => {
    const s =
      proventos?.find?.(
        (i) =>
          i.rubrica_nome?.toLowerCase().includes('salário') &&
          !i.rubrica_nome?.toLowerCase().includes('família')
      ) ?? null;
    return s ? Number(s.valor) : 0;
  })();

  const inssItem =
    descontos?.find?.(
      (i) => i.rubrica_nome?.includes('INSS') && i.rubrica_nome?.includes('Contribuição')
    ) ?? null;

  const salContrInss = inssItem
    ? Number(inssItem.detalhes?.base || totalProventos)
    : Number(totalProventos || 0);

  const fgtsItem =
    informativos?.find?.(
      (i) => i.rubrica_nome?.includes('FGTS') && !i.rubrica_nome?.includes('13')
    ) ?? null;
  const fgtsMes = fgtsItem ? Number(fgtsItem.valor) : 0;

  const irrfItem =
    informativos?.find?.((i) => i.rubrica_nome?.includes('IRRF')) ?? null;
  const baseIrrf = irrfItem ? Number(irrfItem.detalhes?.base || 0) : 0;

  // Inline edit handler for folha-itens
  const handleBlur = (itemId, originalValue) => (ev) => {
    const newVal = parseFloat(ev.target.value) || 0;
    if (newVal !== Number(originalValue)) {
      dpService
        .atualizarFolhaItem(itemId, { valor: newVal })
        .then(() => {
          ev.target.style.borderColor = 'green';
          setTimeout(() => {
            ev.target.style.borderColor = '';
          }, 1500);
        })
        .catch(() => {
          ev.target.style.borderColor = 'red';
        });
    }
  };

  const isEditable = folha && folha.status !== 'fechada';

  return (
    <div className="text-xs print:text-[10px]">
      <div className="border border-gray-400 bg-white text-gray-900">
        {/* ── Company header ── */}
        <div className="flex justify-between items-start p-3 border-b border-gray-400">
          <div>
            <div className="font-bold text-sm">{emp.nome || 'HORIZON GROUP'}</div>
            <div>CNPJ: {emp.cnpj || '—'}</div>
          </div>
          <div className="text-center">
            <div>CC: {func?.departamento?.nome ?? 'GERAL'}</div>
            <div>Mensalista</div>
          </div>
          <div className="text-right">
            <div>Folha Mensal</div>
            <div>{mesAno}</div>
          </div>
        </div>

        {/* ── Employee info row (flex, NOT grid) ── */}
        <div className="flex border-b border-gray-400 text-[10px] w-full">
          {/* Código */}
          <div
            className="p-1 border-r border-gray-400 shrink-0"
            style={{ width: '8%' }}
          >
            <div>
              <div className="text-gray-500">Código</div>
              <div className="font-bold">{func?.matricula ?? '—'}</div>
            </div>
          </div>

          {/* Nome + Cargo */}
          <div className="p-1 border-r border-gray-400 min-w-0 flex-1">
            <div className="min-w-0">
              <div className="text-gray-500">Nome do Funcionário</div>
              <div className="font-bold uppercase truncate">
                {func?.pessoa?.nome ?? '—'}
              </div>
              <div className="truncate">{func?.cargo?.nome ?? '—'}</div>
            </div>
          </div>

          {/* Departamento */}
          <div
            className="p-1 border-r border-gray-400 shrink-0 overflow-hidden"
            style={{ width: '18%' }}
          >
            <div>
              <div className="text-gray-500">Departamento</div>
              <div className="font-bold truncate">
                {func?.departamento?.nome ?? '—'}
              </div>
            </div>
          </div>

          {/* Filial */}
          <div
            className="p-1 border-r border-gray-400 shrink-0"
            style={{ width: '7%' }}
          >
            <div>
              <div className="text-gray-500">Filial</div>
              <div className="font-bold">1</div>
            </div>
          </div>

          {/* Admissão */}
          <div className="p-1 shrink-0" style={{ width: '17%' }}>
            <div>
              <div className="text-gray-500">Admissão</div>
              <div className="font-bold">
                {func?.dt_admissao
                  ? new Date(func.dt_admissao).toLocaleDateString('pt-BR')
                  : '—'}
              </div>
            </div>
          </div>
        </div>

        {/* ── Rubrica table ── */}
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-gray-400 bg-gray-50">
              <th className="text-center p-1 w-12 border-r border-gray-300 font-semibold">
                Código
              </th>
              <th className="text-left p-1 border-r border-gray-300 font-semibold">
                Descrição
              </th>
              <th className="text-right p-1 w-20 border-r border-gray-300 font-semibold">
                Referência
              </th>
              <th className="text-right p-1 w-24 border-r border-gray-300 font-semibold text-green-700">
                Vencimentos
              </th>
              <th className="text-right p-1 w-24 font-semibold text-red-700">
                Descontos
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Proventos rows */}
            {(proventos || []).map((it, idx) => (
              <tr key={idx} className="border-b border-gray-200">
                <td className="text-center p-1 border-r border-gray-200 text-gray-500">
                  {it.rubrica_id || '—'}
                </td>
                <td className="p-1 border-r border-gray-200">{it.rubrica_nome}</td>
                <td className="text-right p-1 border-r border-gray-200 text-gray-600">
                  {fmtRef(it.referencia)}
                </td>
                <td className="text-right p-1 border-r border-gray-200 font-medium text-green-700">
                  {isEditable ? (
                    <input
                      type="number"
                      defaultValue={it.valor}
                      step="0.01"
                      onBlur={handleBlur(it.id, it.valor)}
                      className="w-20 text-right border border-gray-200 rounded px-1 py-0.5 text-xs font-medium text-green-700 focus:outline-none focus:ring-1 focus:ring-teal-500"
                    />
                  ) : (
                    fmtCurrency(it.valor)
                  )}
                </td>
                <td className="text-right p-1" />
              </tr>
            ))}

            {/* Descontos rows */}
            {(descontos || []).map((it, idx) => (
              <tr key={idx + 1000} className="border-b border-gray-200">
                <td className="text-center p-1 border-r border-gray-200 text-gray-500">
                  {it.rubrica_id || '—'}
                </td>
                <td className="p-1 border-r border-gray-200">{it.rubrica_nome}</td>
                <td className="text-right p-1 border-r border-gray-200 text-gray-600">
                  {fmtRef(it.referencia)}
                </td>
                <td className="text-right p-1 border-r border-gray-200" />
                <td className="text-right p-1 font-medium text-red-700">
                  {isEditable ? (
                    <input
                      type="number"
                      defaultValue={it.valor}
                      step="0.01"
                      onBlur={handleBlur(it.id, it.valor)}
                      className="w-20 text-right border border-gray-200 rounded px-1 py-0.5 text-xs font-medium text-red-700 focus:outline-none focus:ring-1 focus:ring-teal-500"
                    />
                  ) : (
                    fmtCurrency(it.valor)
                  )}
                </td>
              </tr>
            ))}

            {/* Spacer row */}
            <tr className="h-6">
              <td colSpan={5} />
            </tr>
          </tbody>
        </table>

        {/* ── Totals row ── */}
        <div className="border-t border-gray-400 grid grid-cols-2 text-right">
          <div className="p-2 border-r border-gray-400">
            <div className="text-gray-500">Total de Vencimentos</div>
            <div className="font-bold text-green-700 text-sm">
              {fmtCurrency(totalProventos)}
            </div>
          </div>
          <div className="p-2">
            <div className="text-gray-500">Total de Descontos</div>
            <div className="font-bold text-red-700 text-sm">
              {fmtCurrency(totalDescontos)}
            </div>
          </div>
        </div>

        {/* ── Líquido ── */}
        <div className="border-t border-gray-400 flex justify-end items-center p-2 gap-3">
          <span className="text-gray-500">Valor Líquido</span>
          <span className="text-2xl">⇒</span>
          <span className="font-bold text-lg">{fmtCurrency(liquido)}</span>
        </div>

        {/* ── Footer bases ── */}
        <div className="border-t border-gray-400 grid grid-cols-6 text-[10px] text-center">
          <div className="p-1 border-r border-gray-300">
            <div className="text-gray-500">Salário Base</div>
            <div className="font-bold">{fmtCurrency(salBase || totalProventos)}</div>
          </div>
          <div className="p-1 border-r border-gray-300">
            <div className="text-gray-500">Sal. Contr. INSS</div>
            <div className="font-bold">{fmtCurrency(salContrInss)}</div>
          </div>
          <div className="p-1 border-r border-gray-300">
            <div className="text-gray-500">Base Cálc. FGTS</div>
            <div className="font-bold">{fmtCurrency(salContrInss)}</div>
          </div>
          <div className="p-1 border-r border-gray-300">
            <div className="text-gray-500">F.G.T.S do Mês</div>
            <div className="font-bold">{fmtCurrency(fgtsMes)}</div>
          </div>
          <div className="p-1 border-r border-gray-300">
            <div className="text-gray-500">Base Cálc. IRRF</div>
            <div className="font-bold">{fmtCurrency(baseIrrf)}</div>
          </div>
          <div className="p-1">
            <div className="text-gray-500">Faixa IRRF</div>
            <div className="font-bold">0,00</div>
          </div>
        </div>

        {/* ── iFood panel ── */}
        <IFoodPanel holerite={holerite} />

        {/* ── Document footer ── */}
        <div className="border-t border-gray-200 p-2 text-center text-gray-400 text-[10px]">
          Documento gerado automaticamente pelo sistema Horizon Group.
        </div>
      </div>
    </div>
  );
}
